import { Grid, Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { isEmpty, first, isFinite } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import SimpleText from 'components/SimpleText';
import { useTranslate } from 'lib/localization/localization';
import WidgetNotAvailable from 'screens/Analysis/AnalysisScreen/WidgetNotAvailable';
import ReturnBreakdownBarChart from 'screens/Analysis/lib/components/ReturnBreakdownBarChart';
import TestSpinner from 'screens/Analysis/lib/components/TestSpinner';
import useDateToggle from 'screens/Analysis/lib/components/useDateToggle';
import { FIRST_LEVEL_HEIGHT } from 'screens/Analysis/lib/constants';
import { useAnalysisQuery } from 'screens/Analysis/lib/queries';
import Breakdown from 'static/images/breakdown.png';

const useStyles = makeStyles(theme => ({
  wrapper: {
    height: '100%',
    minHeight: FIRST_LEVEL_HEIGHT,

    position: 'relative',
  },
  riskBlock: {
    borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
    backgroundColor: theme.palette.primary.main,
    padding: '0.6rem',
    textAlign: 'center',
  },
  barBlock: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#fafafa',
    padding: '0.6rem',
  },
  riskTitle: {
    textAlign: 'center',
    marginTop: '0.8rem',
    marginBottom: '5rem',
  },
  percentageTitle: {
    marginBottom: '0.9rem',
  },
  breakAll: {
    wordSpacing: '9999px',
    wordBreak: 'break-all',
    color: theme.palette.primary.dark,
    [theme.breakpoints.down('xl')]: {
      fontSize: '0.65rem',
    },
    [theme.breakpoints.down('lg')]: {
      fontSize: '0.5rem',
    },
  },
  bar: {
    fontSize: '0.9rem',
    border: `1px solid ${theme.palette.primary.dark}`,
    padding: '0rem 3.3rem',
    minHeight: '0.95rem',
    color: '#5d5c80',
    display: 'inline-block',
    position: 'relative',
    flex: '1',
    [theme.breakpoints.down('xl')]: {
      padding: '0rem 1.75rem',
    },
    [theme.breakpoints.down('lg')]: {
      padding: '0rem 0.9rem',
    },
    '&:before': {
      content: '""',
      display: 'inline-block',
      height: '100%',
      verticalAlign: 'middle',
    },
  },
  positiveBorder: {
    alignSelf: 'flex-end',
    verticalAlign: 'middle',
  },
  negativeBorder: {
    alignSelf: 'flex-start',
    top: '100%',
  },
}));

const barLineOptions = ['stdDev', 'stdDevB', 'mdd', 'mddB'];

const RiskReturnBreakdownBar = ({ analysisId }) => {
  const classes = useStyles();

  const translate = useTranslate();
  const { analysis } = useAnalysisQuery(analysisId);
  const widgetData = analysis?.result?.RiskBreakdownBar ?? {};

  const [dateFilter, DateFilterComponent] = useDateToggle(
    !isEmpty(widgetData) ? first(widgetData.filters) : 'all'
  );

  let painIdx;
  let beta;
  if (!isEmpty(widgetData)) {
    painIdx = widgetData.data[dateFilter].block.painIdx;
    if (isFinite(painIdx)) {
      painIdx = painIdx.toFixed(2);
    } else {
      painIdx = null;
    }
    beta = widgetData.data[dateFilter].block.beta;
    if (isFinite(beta)) {
      beta = beta.toFixed(2);
    } else {
      beta = null;
    }
  }

  return (
    <Grid item xs={12} lg={9}>
      <Paper className={classes.wrapper}>
        <Grid container style={{ height: '100%' }}>
          {!isEmpty(widgetData) && isEmpty(widgetData.filters) ? (
            <WidgetNotAvailable image={Breakdown} />
          ) : (
            <>
              {isEmpty(widgetData) ? (
                <TestSpinner />
              ) : (
                <>
                  <DateFilterComponent
                    available={widgetData.filters}
                    errors={widgetData.errors}
                    hints={widgetData.hints}
                  />
                  <Grid item xs={3} className={classes.riskBlock}>
                    <SimpleText
                      translateId='analysis.portfolioRisk'
                      variant='h2'
                      sx={{ color: 'primary.contrastText' }}
                      className={classes.riskTitle}
                    />
                    <Grid
                      container
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                      style={{ textAlign: 'center' }}
                    >
                      <Grid item xs={6}>
                        <SimpleText
                          text={painIdx && `${painIdx} %`}
                          translateId={painIdx ? null : 'analysis.valueUndefined'}
                          variant='h2'
                          sx={{ color: 'primary.contrastText' }}
                          className={classes.percentageTitle}
                        />
                        <SimpleText
                          translateId='analysis.painIdx'
                          sx={{ color: 'primary.contrastText' }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <SimpleText
                          text={beta && `${beta}`}
                          translateId={beta ? null : 'analysis.valueUndefined'}
                          variant='h2'
                          sx={{ color: 'primary.contrastText' }}
                          className={classes.percentageTitle}
                        />
                        <SimpleText
                          translateId='analysis.beta'
                          sx={{ color: 'primary.contrastText' }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid xs={9}>
                    <div style={{ height: '99%', width: '100%', position: 'relative' }}>
                      <ReturnBreakdownBarChart
                        data={barLineOptions.map(key => {
                          const value = widgetData.data[dateFilter].block[key];
                          return {
                            key: translate(`analysis.${key}`),
                            [translate('analysis.value')]: (value * 100).toFixed(2),
                          };
                        })}
                        padding={0.1}
                      />
                    </div>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </Paper>
    </Grid>
  );
};

RiskReturnBreakdownBar.propTypes = {
  analysisId: PropTypes.string,
};

RiskReturnBreakdownBar.defaultProps = {
  analysisId: undefined,
};

export default RiskReturnBreakdownBar;
